import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3, TrendingUp, TrendingDown, Activity, Cpu, Database,
  AlertCircle, CheckCircle2, Clock, Zap, Target, Shield, HardDrive,
  Network, RefreshCw, EyeOff, Play, Pause
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import toast from 'react-hot-toast';

const PerformanceMetrics = () => {
  const [performanceData, setPerformanceData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [optimizationSuggestions, setOptimizationSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showAlerts, setShowAlerts] = useState(true);
  const [showOptimizations, setShowOptimizations] = useState(true);

  // Create AdminService instance (memoized to avoid recreating on each render)
  const adminService = useMemo(() => new AdminService(), []);

  const loadPerformanceData = useCallback(async (showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const [metricsResponse, alertsResponse, optimizationsResponse] = await Promise.all([
        adminService.getPerformanceMetrics(selectedTimeRange),
        adminService.getPerformanceAlerts(),
        adminService.getOptimizationSuggestions()
      ]);
      
      if (metricsResponse.success) {
        setPerformanceData(metricsResponse.data);
      } else {
        if (showToast) {
          toast.error(metricsResponse.message || 'Failed to load performance metrics');
        }
      }
      
      if (alertsResponse.success) {
        setAlerts(alertsResponse.data || []);
      }
      
      if (optimizationsResponse.success) {
        setOptimizationSuggestions(optimizationsResponse.data || []);
      }
      
      if (showToast) {
        toast.success('Performance metrics refreshed successfully');
      }
    } catch (error) {
      console.error('Error loading performance data:', error);
      if (showToast) {
        toast.error('Failed to refresh performance metrics');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedTimeRange, adminService]);

  useEffect(() => {
    loadPerformanceData();
    
    if (autoRefresh) {
      const interval = setInterval(() => loadPerformanceData(false), 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [loadPerformanceData, autoRefresh]);

  const handleOptimizationApply = async (optimizationId) => {
    try {
      const response = await adminService.applyOptimization(optimizationId);
      if (response.success) {
        toast.success('Optimization applied successfully!');
        await loadPerformanceData(false);
      } else {
        toast.error(response.message || 'Failed to apply optimization');
      }
    } catch (error) {
      console.error('Error applying optimization:', error);
      toast.error('Failed to apply optimization');
    }
  };

  const handleAlertAcknowledge = async (alertId) => {
    try {
      const response = await adminService.acknowledgeAlert(alertId);
      if (response.success) {
        setAlerts(prev => prev.filter(alert => alert.id !== alertId));
        toast.success('Alert acknowledged');
      } else {
        toast.error(response.message || 'Failed to acknowledge alert');
      }
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      toast.error('Failed to acknowledge alert');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      healthy: 'text-green-600 bg-green-100',
      warning: 'text-yellow-600 bg-yellow-100',
      critical: 'text-red-600 bg-red-100',
      maintenance: 'text-blue-600 bg-blue-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const getAlertSeverityColor = (severity) => {
    const colors = {
      low: 'text-blue-600 bg-blue-100',
      medium: 'text-yellow-600 bg-yellow-100',
      high: 'text-orange-600 bg-orange-100',
      critical: 'text-red-600 bg-red-100'
    };
    return colors[severity] || 'text-gray-600 bg-gray-100';
  };

  const getOptimizationImpactColor = (impact) => {
    const colors = {
      low: 'text-blue-600 bg-blue-100',
      medium: 'text-yellow-600 bg-yellow-100',
      high: 'text-green-600 bg-green-100',
      critical: 'text-purple-600 bg-purple-100'
    };
    return colors[impact] || 'text-gray-600 bg-gray-100';
  };

  if (loading && !performanceData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading performance metrics...</span>
        </div>
      </div>
    );
  }

  if (!performanceData) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">No performance data available.</p>
        <Button
          variant="outline"
          onClick={() => loadPerformanceData(true)}
          className="mt-4"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  // Safely extract data with defaults
  const systemStatus = performanceData?.systemStatus || [];
  const applicationMetrics = performanceData?.applicationMetrics || [];
  const databaseMetrics = performanceData?.databaseMetrics || [];
  const historyMetrics = performanceData?.historyMetrics || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Performance Metrics</h1>
          <p className="text-gray-600 dark:text-gray-400">Monitor system performance and optimize operations</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="p-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <Button
            variant={autoRefresh ? "default" : "secondary"}
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-2 ${
              autoRefresh ? 'bg-green-600 hover:bg-green-700 text-white' : ''
            }`}
          >
            {autoRefresh ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {autoRefresh ? 'Auto Refresh On' : 'Auto Refresh Off'}
          </Button>
          <Button
            variant="outline"
            onClick={() => loadPerformanceData(true)}
            disabled={loading || refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${(loading || refreshing) ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* System Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">System Status Overview</CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400">Real-time system health indicators</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {systemStatus.length > 0 ? systemStatus.map((status, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    {status.component === 'CPU' && <Cpu className="w-5 h-5 text-blue-600 mr-2" />}
                    {status.component === 'Memory' && <HardDrive className="w-5 h-5 text-green-600 mr-2" />}
                    {status.component === 'Database' && <Database className="w-5 h-5 text-purple-600 mr-2" />}
                    {status.component === 'Network' && <Network className="w-5 h-5 text-orange-600 mr-2" />}
                    <h4 className="font-medium text-gray-900">{status.component}</h4>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(status.status)}`}>
                    {status.status}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Usage</span>
                    <span className="font-medium">{status.usage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        status.usage > 80 ? 'bg-red-500' : status.usage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${status.usage}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Response Time: {status.responseTime}ms</span>
                    <span>Uptime: {status.uptime}</span>
                  </div>
                </div>
              </div>
            )) : (
              <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                <Activity className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p>No system status data available</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Application Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">Application Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {applicationMetrics.length > 0 ? applicationMetrics.map((metric, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-gray-800">
                  <div className="flex items-center">
                    {metric.type === 'response_time' && <Clock className="w-5 h-5 text-blue-600 mr-3" />}
                    {metric.type === 'throughput' && <Zap className="w-5 h-5 text-green-600 mr-3" />}
                    {metric.type === 'error_rate' && <AlertCircle className="w-5 h-5 text-red-600 mr-3" />}
                    {metric.type === 'availability' && <Shield className="w-5 h-5 text-purple-600 mr-3" />}
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{metric.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{metric.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{metric.value}</p>
                    <div className="flex items-center text-sm">
                      {metric.trend > 0 ? (
                        <TrendingUp className="text-green-500 mr-1" size={16} />
                      ) : (
                        <TrendingDown className="text-red-500 mr-1" size={16} />
                      )}
                      <span className={metric.trend > 0 ? 'text-green-600' : 'text-red-600'}>
                        {Math.abs(metric.trend)}%
                      </span>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Activity className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No application metrics available</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Database Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">Database Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {databaseMetrics.length > 0 ? databaseMetrics.map((metric, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-gray-800">
                  <div className="flex items-center">
                    <Database className="w-5 h-5 text-blue-600 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{metric.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{metric.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{metric.value}</p>
                    <div className="flex items-center text-sm">
                      {metric.trend > 0 ? (
                        <TrendingUp className="text-green-500 mr-1" size={16} />
                      ) : (
                        <TrendingDown className="text-red-500 mr-1" size={16} />
                      )}
                      <span className={metric.trend > 0 ? 'text-green-600' : 'text-red-600'}>
                        {Math.abs(metric.trend)}%
                      </span>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Database className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No database metrics available</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {showAlerts && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">Performance Alerts</CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400">{alerts.length} active alerts</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAlerts(!showAlerts)}
                className="text-gray-400 hover:text-gray-600"
              >
                <EyeOff className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {alerts.length > 0 ? (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <div key={alert.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <AlertCircle className={`w-5 h-5 mr-3 ${
                        alert.severity === 'critical' ? 'text-red-500' :
                        alert.severity === 'high' ? 'text-orange-500' :
                        alert.severity === 'medium' ? 'text-yellow-500' : 'text-blue-500'
                      }`} />
                      <div>
                        <p className="font-medium text-gray-900">{alert.title}</p>
                        <p className="text-sm text-gray-600">{alert.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(alert.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getAlertSeverityColor(alert.severity)}`}>
                        {alert.severity}
                      </span>
                      <Button
                        onClick={() => handleAlertAcknowledge(alert.id)}
                        variant="outline"
                        size="sm"
                        className="text-blue-600 bg-blue-50 hover:bg-blue-100"
                      >
                        Acknowledge
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500" />
                <p>No active alerts</p>
                <p className="text-sm">System is running smoothly</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Optimization Suggestions */}
      {showOptimizations && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">Optimization Suggestions</CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400">{optimizationSuggestions.length} suggestions available</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowOptimizations(!showOptimizations)}
                className="text-gray-400 hover:text-gray-600"
              >
                <EyeOff className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {optimizationSuggestions.length > 0 ? (
              <div className="space-y-4">
                {optimizationSuggestions.map((suggestion) => (
                  <div key={suggestion.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center">
                        <Target className="w-5 h-5 text-blue-600 mr-3" />
                        <div>
                          <h4 className="font-medium text-gray-900">{suggestion.title}</h4>
                          <p className="text-sm text-gray-600">{suggestion.description}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getOptimizationImpactColor(suggestion.impact)}`}>
                        {suggestion.impact} impact
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        <p>Expected improvement: {suggestion.expectedImprovement}</p>
                        <p>Implementation effort: {suggestion.effort}</p>
                      </div>
                      <Button
                        onClick={() => handleOptimizationApply(suggestion.id)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        size="sm"
                      >
                        Apply Optimization
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Target className="w-12 h-12 mx-auto mb-4 text-blue-500" />
                <p>No optimization suggestions</p>
                <p className="text-sm">System is already optimized</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Performance History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">Performance History</CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400">Historical performance trends</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {historyMetrics.length > 0 ? historyMetrics.map((metric, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">{metric.name}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Current</span>
                    <span className="font-medium">{metric.current}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Average</span>
                    <span className="font-medium">{metric.average}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Peak</span>
                    <span className="font-medium">{metric.peak}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${(metric.current / metric.peak) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )) : (
              <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                <BarChart3 className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p>No performance history available</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceMetrics;
